import React, { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../Tooltip';
import { ArrowRight } from 'lucide-react';
import TableControl, {
  SelectedFilters,
  TableControlProps,
} from './TableControls';

export type ColumnType = 'text' | 'button' | 'dropdown';

export type Column<T> = {
  key: keyof T;
  label: React.ReactNode | string;
  type: ColumnType;
  options?: string[];
  onClick?: (row: T) => void;
  onChange?: (value: T[keyof T], row: T) => void;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  showTooltip?: boolean;
};

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  tableControls?: TableControlProps<T>;
  showActionArrow?: boolean;
}

const DataTable = <T,>({
  data,
  columns,
  onRowClick,
  tableControls,
  showActionArrow = true,
}: DataTableProps<T>) => {
  const [filters, setFilters] = useState<SelectedFilters<T>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  let noData = 'No data available';
  const page = location.href;
  if(page.includes('audit-log')){
    noData = 'No audit log entries found for the selected criteria'
  }
  const renderText = (value: T[keyof T], column: Column<T>) => {
    if (typeof value !== 'string' || value.trim().length === 0) {
      return <>{value as React.ReactNode}</>;
    }
  
    const shouldShowTooltip = value.length > 75 && column.showTooltip;
    const truncatedText = value.slice(0, 75) + (shouldShowTooltip ? '...' : '');
  
    if (shouldShowTooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="truncate max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
              {truncatedText}
            </TooltipTrigger>
            <TooltipContent className="bg-foreground text-background p-2 max-w-xs">
              <p>{value}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
  
    return <>{value}</>;
  };

  const renderButton = (column: Column<T>, value: T[keyof T], row: T) => {
    return (
      <button
        onClick={() => column.onClick && column.onClick(row)}
        className="px-2 py-1 border rounded"
      >
        {String(value)}
      </button>
    );
  };

  const renderDropdown = (column: Column<T>, value: T[keyof T], row: T) => {
    return (
      <select
        onChange={(e) =>
          column.onChange && column.onChange(e.target.value as T[keyof T], row)
        }
        className="px-2 py-1 border rounded"
        data-testid={`dropdown-${value}`}
      >
        {column?.options?.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  };

  const handleSort = (key: string) => {
    setSortConfig((prevSortConfig) => {
      const direction = prevSortConfig?.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  const sortedData: T[] = useMemo(() => {
    let sortableData = [...data];

    if (sortConfig !== null) {
      const { key, direction } = sortConfig;
      sortableData = sortableData.sort((a, b) => {
        const aValue = a[key as keyof T];
        const bValue = b[key as keyof T];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        return 0;
      });
    }

    return sortableData;
  }, [data, sortConfig]);

  const tableData: T[] = useMemo(() => {
    const filteredRows = sortedData.filter((d: T) =>
      Object.entries(filters).every(([key, values]) => {
        if (Array.isArray(values)) {
          return (
            values.length === 0 ||
            values.includes(d[key.toLowerCase() as keyof T])
          );
        }
        return true;
      })
    );

    if (searchTerm.trim() !== '') {
      const regex = new RegExp(searchTerm, 'i');
      return filteredRows.filter((fr) => {
        for (const key in fr) {
          if (typeof fr[key] === 'string' && regex.test(fr[key])) return true;
        }
        return false;
      });
    }

    return filteredRows;
  }, [searchTerm, filters, sortedData]);

  return (
    <>
      {tableControls && (
        <TableControl
          tableTitle={tableControls.tableTitle}
          enableSearch={tableControls.enableSearch}
          filters={tableControls.filters}
          onFilterSelected={(filters) => setFilters(filters)}
          selectedFilters={filters}
          searchFor={searchTerm}
          updateSearch={(value) => setSearchTerm(value)}
          tableButtons={tableControls.tableButtons}
        />
      )}
      <Table className="border-[1px] border-borderGray bg-white">
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key as string}
                className="flex-1 cursor-pointer"
                onClick={() => handleSort(column.key as string)}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody >
          {tableData.length ? (
            tableData.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                className={`items-center ${
                  onRowClick ? 'cursor-pointer' : ''
                }`}
                data-testid={`data-table-card-${rowIndex}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((column) => {
                  const value = row[column.key];

                  return (
                    <TableCell key={column.key as string}>
                      {column.render ? (
                        column.render(value, row)
                      ) : (
                        <>
                          {column.type === 'text' && renderText(value, column)}

                          {column.type === 'button' &&
                            renderButton(column, value, row)}

                          {column.type === 'dropdown' &&
                            column.options &&
                            renderDropdown(column, value, row)}
                        </>
                      )}
                    </TableCell>
                  );
                })}
                {onRowClick && showActionArrow && (
                  <TableCell className="cursor-pointer text-foreground flex justify-end">
                    <ArrowRight size={16} color="#495AE4"/>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center">
               {noData}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

export { DataTable };
